import { googleDarkDisabled, googleDarkNormal, googleGSVG } from "#webapp/src/assets/img/google";
import { useGoogleOAuth } from "#webapp/src/auth/google-auth.util";
import { Button } from "@clockwise/design-system";
import { SvgIconComponent } from "@clockwise/design-system/icons";
import { Size, TextAlignment } from "@clockwise/design-system/src/components/Button";
import { Sentiment } from "@clockwise/design-system/src/constants/types";
import {
  getInitialAuthTrackingFunnel,
  getStepBasedOnTrackingFunnel,
} from "@clockwise/web-commons/src/util/analytics.util";
import { Button as MUIButton, makeStyles } from "@material-ui/core";
import React, { CSSProperties } from "react";
import { styles } from "./google-btn.styles";

const useStyles = makeStyles(styles);

export type GoogleButtonVariant = "flat" | "googleStandard" | "googleSimple" | "default";

interface Props {
  variant?: GoogleButtonVariant;
  fullWidth?: boolean;
  icon?: SvgIconComponent;
  label: string;
  style?: CSSProperties;
  labelStyle?: CSSProperties;
  disabled?: boolean;
  textAlign?: TextAlignment;
  sentiment?: Sentiment;
  size?: Size;

  onSuccess: (response: { code: string }) => void;
  onFailure: (error: { error: string }) => void;
  onClick?: React.MouseEventHandler;

  excludeAdminPermissions?: boolean;
  excludeContactPermission?: boolean;
}

const GoogleBtn = ({
  disabled = false,
  label,
  fullWidth = false,
  icon,
  labelStyle,
  style,
  variant = "default",
  textAlign = "center",
  sentiment = "neutral",
  size = "medium",
  onSuccess,
  onFailure,
  excludeContactPermission,
  excludeAdminPermissions,
}: Props) => {
  const classes = useStyles();

  const { prompt } = useGoogleOAuth();
  const scopes: string[] = React.useMemo(() => {
    const scopes = [
      "https://www.googleapis.com/auth/calendar",
      "https://www.googleapis.com/auth/userinfo.email",
      "https://www.googleapis.com/auth/userinfo.profile",
    ];

    if (!excludeContactPermission) {
      scopes.push("https://www.googleapis.com/auth/contacts.readonly");
    }
    if (!excludeAdminPermissions) {
      scopes.push(
        "https://www.googleapis.com/auth/admin.directory.user.readonly",
        "https://www.googleapis.com/auth/admin.directory.resource.calendar.readonly",
      );
    }
    return scopes;
  }, [excludeContactPermission, excludeAdminPermissions]);

  const handleClick = () => {
    // google tag manager
    const dataLayer = window && (window as any).dataLayer;
    const tracking_funnel = getInitialAuthTrackingFunnel();
    const tracking_step = getStepBasedOnTrackingFunnel(tracking_funnel, "click_to_oauth");
    dataLayer.push({ event: "userClickedLoginBtn", ecosystem: "web" });
    // new GTM event for user login, dont delete one above until we confirm everything works correctly
    dataLayer.push({
      event: "funnel_step",
      universal_step: "OAUTH",
      funnel_name: tracking_funnel,
      step_name: "click_to_oauth",
      step_number: tracking_step,
      funnel_option: "google",
    });
    prompt(scopes, { onSuccess, onFailure });
  };

  switch (variant) {
    case "flat":
      return (
        <Button
          variant="text"
          startIcon={icon}
          onClick={handleClick}
          disabled={disabled}
          fullWidth={fullWidth}
          textAlign={textAlign}
          sentiment={sentiment}
          size={size}
        >
          {label}
        </Button>
      );
    case "googleStandard":
      return (
        <MUIButton
          variant="contained"
          className={classes.googleButton}
          style={{ ...style }}
          TouchRippleProps={{ classes: { ripple: classes.rippleRoot } }}
          disabled={disabled}
          onClick={handleClick}
          fullWidth={fullWidth}
        >
          <img style={{ marginLeft: 0 }} src={disabled ? googleDarkDisabled : googleDarkNormal} />
          <span style={{ ...labelStyle }}>{label}</span>
        </MUIButton>
      );
    case "googleSimple":
      return (
        <Button
          variant="outlined"
          disabled={disabled}
          onClick={handleClick}
          fullWidth={fullWidth}
          textAlign={textAlign}
          sentiment={sentiment}
          size={size}
        >
          <img width="20" height="20" className="cw-mr-[10px]" src={googleGSVG} />
          <span style={{ ...labelStyle }}>{label}</span>
        </Button>
      );
    default:
      return (
        <Button
          variant="outlined"
          startIcon={icon}
          disabled={disabled}
          onClick={handleClick}
          fullWidth={fullWidth}
          textAlign={textAlign}
          sentiment={sentiment}
          size={size}
        >
          {label}
        </Button>
      );
  }
};

export default GoogleBtn;
